import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogTypeData, CreateCatalogMutation } from 'gql/graphql';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createCatalogGql } from '../createCatalogQueries';

const CREATE_CATALOG_REQUEST_ID = 'createCatalog';

export const useCreateCatalog = ({
    closeModalHandler,
    updateCatalogList,
}: {
    closeModalHandler: () => void;
    updateCatalogList: () => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [resetCreateData] = useResetStoreData({ requestId: CREATE_CATALOG_REQUEST_ID });

    const tempSuccessHandler = useCallback(
        (props: TActionCommonInterface<CreateCatalogMutation>) => {
            const { payload, reducerId } = props;

            if (reducerId === CREATE_CATALOG_REQUEST_ID) {
                const {
                    createCatalog: { data },
                } = payload ?? {};

                if (data) {
                    navigate(`/catalogs/${data?.type?.uniqueId}/${data?.uniqueId}`);
                }
            }

            closeModalHandler();
            updateCatalogList();
            resetCreateData(undefined);
        },
        [closeModalHandler, navigate, resetCreateData, updateCatalogList],
    );
    const [createCatalogData, createCatalogStatus, , createCatalogDataRequest] = useRequest({
        requestId: CREATE_CATALOG_REQUEST_ID,
        query: createCatalogGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    const createCatalogHandler = ({ createCatalogType }: { createCatalogType: CatalogTypeData }) => {
        const { name, uniqueId } = createCatalogType;

        dispatch(
            createCatalogDataRequest({
                catalogInput: {
                    name,
                    typeUniqueId: uniqueId,
                },
            }),
        );
    };

    return {
        createCatalogData: createCatalogData?.createCatalog?.data,
        createCatalogStatus,
        createCatalogHandler,
    };
};
